(ns app.front.view.admin.commands.remove-user-from-ws
  (:require [app.admin.user-remove :as user-remove]
            [app.domain.municipality.model :as municipality-model]
            [app.domain.util :as du]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.section :as section]
            [app.front.util :as futil :refer [for-frag]]
            [app.front.view.group.admin.common :as acommon]
            [clojure.string :as str]
            [core.db.entity.tree :as entity-tree]
            [core.db.entity.util :as eu]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.user.model :as user-model]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]))

(rf/reg-event-db ::set-email
  (rf/path [::state])
  (fn [state [_ new-email]]
    (-> state
        (assoc :email new-email)
        (assoc :selected-workspaces #{})
        (assoc :selected-groups #{})
        (dissoc :user))))

(rf/reg-sub ::state :-> ::state)

(rf/reg-sub ::email
  :<- [::state]
  (fn [state _] (:email state "")))

(rf/reg-sub ::user
  :<- [::state]
  :-> :user)

(rf/reg-sub ::user-id
  :<- [::user]
  :-> :db/id)

(rf/reg-sub ::email-valid?
  :<- [::email]
  (fn [email _]
    (not (str/blank? email))))

(rf/reg-event-fx ::find-user
  (fn [_ [_ email]]
    {:comm/command {:command    :admin/get-user-workspaces-and-groups
                    :method     :get
                    :data       (util/mmap email)
                    :on-success [::find-user.success]}}))

(rf/reg-event-fx ::find-user.success
  (fn [{:keys [db]} [_ status {:keys [user entities]}]]
    (if (= 200 status)
      {:db       (-> db
                     (assoc-in [::state :user] user)
                     (assoc-in [::state :selected-workspaces] #{})
                     (assoc-in [::state :selected-groups] #{}))
       :dispatch [::entities/merge-entity-db
                  status
                  (-> (du/entities->entity-db entities)
                      (assoc (:db/id user) user))]}
      {:db db})))

(rf/reg-event-fx ::revoke-access-to-workspaces
  (fn [_ [_ user-id workspace-ids]]
    {:comm/command {:command    :admin/revoke-user-access-for-workspaces
                    :data       (util/mmap user-id workspace-ids)
                    :on-success [::revoke-access-to-workspaces.success]
                    :notify     {:success "Access to workspaces successfully revoked"}}}))

(rf/reg-event-fx ::remove-user-from-groups
  (fn [_ [_ user-id group-ids]]
    {:comm/command {:command    :group/edit-user-groups
                    :data       (util/mmap {:remove (vec group-ids) :add []} user-id)
                    :on-success [::remove-user-from-groups.success]
                    :notify     {:success "Group memberships successfully removed"}}}))

(defn handle-remove-success-fn [k]
  (fn [{:keys [db]} [_ status resp]]
    {:db       (-> db
                   (assoc-in [::state k] #{}))
     :dispatch [::entities/apply-changes status resp]}))

(rf/reg-event-fx ::revoke-access-to-workspaces.success
  (handle-remove-success-fn :selected-workspaces))

(rf/reg-event-fx ::remove-user-from-groups.success
  (fn [{:keys [db]} [_ status _]]
    (when (= 200 status)
      {:db       (-> db
                     (assoc-in [::state :selected-groups] #{}))
       :dispatch [::find-user (-> db ::state :email)]})))

(defn toggle [s val]
  ((if (contains? s val) disj conj) s val))

(defn handle-toggle-fn [k]
  (fn [state [_ id]]
    (-> state
        (update k toggle id))))

(rf/reg-event-db ::toggle-workspace
  (rf/path [::state])
  (handle-toggle-fn :selected-workspaces))

(rf/reg-event-db ::toggle-group
  (rf/path [::state])
  (handle-toggle-fn :selected-groups))

(rf/reg-sub ::selected-workspaces
  :<- [::state]
  :-> :selected-workspaces)

(rf/reg-sub ::selected-groups
  :<- [::state]
  :-> :selected-groups)

(defn handle-selected? [selected [_ workspace-id]]
  (contains? selected workspace-id))

(rf/reg-sub ::selected-workspace?
  :<- [::selected-workspaces]
  handle-selected?)

(rf/reg-sub ::selected-group?
  :<- [::selected-groups]
  handle-selected?)

(rf/reg-sub ::parties-and-invites
  :<- [::entities/context]
  :<- [::user-id]
  (fn [[ctx user-id] _]
    (when user-id
      (-> (concat
            (entities/find-entities ctx {:party/user user-id})
            (entities/find-entities ctx {:invite/user user-id}))
          distinct))))

(rf/reg-sub ::workspaces-by-project
  :<- [::parties-and-invites]
  (fn [parties-and-invites _]
    (->> parties-and-invites
         (map (fn [party-or-invite]
                (->> (or (-> party-or-invite :invite/application entity-tree/lazy-ancestors)
                         (-> party-or-invite entity-tree/lazy-ancestors))
                     (filter (eu/is-type-fn :entity.type/workspace))
                     first)))
         distinct
         (group-by :entity/parent)
         (sort-by (comp :project/name first)))))

(rf/reg-sub ::groups
  :<- [::user]
  (fn [user _]
    (<sub [::entities/entities (:user/groups user)])))

(def group-id+name
  (juxt :db/id :group/name))

(defn first-parent-group-of-type-fn [types]
  (letfn [(f [group]
            (or (when (contains? types (:group/type group))
                  group)
                (some-> group :entity/parent f)
                group))]
    f))

(def first-parent-or-domain-group
  (first-parent-group-of-type-fn #{:group.type/domain}))

(def first-auth-or-domain-group
  (first-parent-group-of-type-fn #{:group.type/domain
                                   :group.type/application-authority
                                   :group.type/organizational-unit}))

(rf/reg-sub ::selected-group-entities
  :<- [::selected-groups]
  (fn [group-ids _]
    (<sub [::entities/entities group-ids])))

(rf/reg-sub ::selected-groups-formatted
  :<- [::selected-group-entities]
  (fn [groups _]
    (->> groups
         (map (fn [group]
                (str/join " / "
                          (distinct [(-> group first-parent-or-domain-group :group/name)
                                     (-> group first-auth-or-domain-group :group/name)
                                     (:group/name group)]))))
         sort)))

(rf/reg-sub ::grouped-groups
  :<- [::groups]
  (fn [groups _]
    (->> groups
         (group-by first-auth-or-domain-group)
         (map (fn [[k v]]
                [[(:group/name k)
                  (-> k first-parent-or-domain-group :group/subtype)]
                 (map group-id+name v)]))
         (into {}))))

(rf/reg-sub ::has-auths-or-invites?
  (fn [[_ workspace-id]]
    [(rf/subscribe [::user-id])
     (rf/subscribe [::entities/entity {:workspace/id workspace-id}])])
  (fn [[user-id workspace] _]
    (boolean
      (or
        (->> workspace
             eu/descendants
             (some (fn [e]
                     (->> e
                          :authorization/_target
                          (some (comp (partial = user-id)
                                      :db/id
                                      :authorization/subject)))))
             boolean)
        (seq (user-remove/get-invites user-id workspace))))))

(rf/reg-sub ::roles
  (fn [[_ entity-id]]
    [(rf/subscribe [::entities/entity entity-id])
     (rf/subscribe [::user-id])])
  (fn [[entity user-id] _]
    (some->> entity
             :authorization/_target
             (filter (fn [auth] (-> auth
                                    :authorization/subject
                                    :db/id
                                    (= user-id))))
             (map #(:authorization/role %
                                        ;; also keep auths wo/ role
                                        "other"))
             (remove nil?)
             (map str)
             seq
             (str/join ", "))))

(defn item [{:keys [done? selected? on-change id tight?]} & children]
  [:li
   {:class [(when selected? "bg-ui-night-sky-30 rounded")
            (when-not tight? "mb-2")]}
   (into (if (not done?)
           [:label.flex.flex-column {:class (if tight? "items-center" "p-2")}
            [:div.mr-2
             (cc/checkbox {:id         (str "select-for-remove-" id)
                           :label-text "Select for remove"
                           :hide-label true
                           :checked    (boolean selected?)
                           :on-change  on-change})]]
           [:div.flex.flex-column
            [:div.p-2.text-xl "✓"]])
         children)])

(defn item-group [{:keys [title subtitle details]} & children]
  (into [:li.mb-2
         [:div.flex.items-baseline.flex-wrap
          [:h4.mb-1 title]
          [:span.ml-4 subtitle]
          [:span.ml-4.font-mono details]]]
        children))

(defn workspace [{:workspace/keys [id permit-type address category municipality municipal-case-id]
                  :domain/keys    [state]
                  :as             workspace}]
  (let [tr   (<sub [:tr])
        lang (<sub [:language])]
    [item
     (util/mmap {:selected? (<sub [::selected-workspace? id])
                 :done?     (not (<sub [::has-auths-or-invites? id]))
                 :on-change #(>evt [::toggle-workspace id])}
                id)
     [:div
      [:div.flex.items-baseline
       [:h5.w-64 id]
       [:span.ml-2 (tr [permit-type])]
       (when category [:span.ml-1 " / " (tr [category])])]
      [:div municipal-case-id]
      [:div address ", " (-> municipality (municipality-model/municipality-name lang))]
      [:div "State: " (tr [state])]
      [:div "Roles: " (<sub [::roles (:db/id workspace)])]
      (->> workspace
           :entity/_parent
           (map (fn [e] (when-let [roles (<sub [::roles (:db/id e)])]
                          [:li.my-2
                           (tr [(:entity/type e)]) " - " (tr [(:domain/state e)])
                           [:div "roles: " roles]])))
           (into [:ul.ml-4]))]]))

(defn project [[project workspaces]]
  (let [lang (<sub [:language])]
    [item-group
     {:title    (or (:project/name project) [:span.text-ui-night-sky-70 "- no project -"])
      :subtitle (-> project
                    :project/municipality
                    (municipality-model/municipality-name lang))
      :details  (<sub [::roles (:db/id project)])}
     (->>  workspaces
           (map workspace)
           (into [:ul.ml-4.font-mono]))]))

(defn modal-trigger [{:keys [id items on-yes-click trigger-button-text]} & contents]
  (let [tr                 (<sub [:tr])
        {email :user/email
         :as   user}       (<sub [::user])]
    [ui/modal-confirmation-button
     (util/mmap id tr trigger-button-text on-yes-click)
     [:div
      (into [:<>] contents)
      [:div.font-bold.mt-2 "User: " email " / " (user-model/full-name user)]
      (into [:ul.list-disc.ml-4.mt-4]
            (map (fn [ws] [:li ws]) items))]]))

(defn workspaces []
  (let [all-workspaces      (<sub [::workspaces-by-project])
        selected-workspaces (<sub [::selected-workspaces])
        user-id             (<sub [::user-id])]
    (when (seq all-workspaces)
      [section/subsection
       {:title "Remove user from workspace"}
       [:div
        [:div.mb-2 "Workspaces that the user is a party of, or invited to are listed below. Select the workspaces from which you would like to remove the user."]
        (->> all-workspaces
             (map project)
             (into [:ul]))
        [modal-trigger {:id                  "perform-remove-user"
                        :trigger-button-text "Remove user from workspaces"
                        :items               selected-workspaces
                        :on-yes-click        #(>evt [::revoke-access-to-workspaces user-id selected-workspaces])}
         "Are you sure you want to mark the user as removed on the selected workspaces and revoke their access?"]]])))

(defn groups []
  (let [groups          (<sub [::grouped-groups])
        user-id         (<sub [::user-id])
        selected-groups (<sub [::selected-groups])]
    (when (seq groups)
      [section/subsection
       {:title "Remove user from groups"}
       [:<>
        [:ul
         (for-frag [[[parent-name parent-type] user-groups] groups]
           [item-group {:title   parent-name
                        :details (str parent-type)}
            [:ul
             (for-frag [[id group-name] user-groups]
               [item (util/mmap {:on-change #(>evt [::toggle-group id])
                                 :selected? (<sub [::selected-group? id])
                                 :tight?    true}
                                id)
                group-name])]])]
        [modal-trigger {:id                  "perform-remove-from-group"
                        :trigger-button-text "Remove user from groups"
                        :items               (<sub [::selected-groups-formatted])
                        :on-yes-click        #(>evt [::remove-user-from-groups user-id selected-groups])}
         [:div "Are you sure you want to remove the user from these groups?"]
         [:div.mt-2 [:span.bold.mr-2 "NOTE"]
          "Some additional groups may be removed and other changes done (e.g. restore dispatched inspections to the booking queue)"]]]])))

(defn help-text []
  [:section
   [:p
    "This tool allows you to revoke a user's access to a workspace."
    [:span.mx-1.font-bold "Do not"] "use this tool just based on an end user request -"
    [:span.mx-1.font-bold "always"] "confirm with municipality!"]

   [:p "It will do do the following steps:"]
   [:ul.list-disc.ml-6.mb-4
    [:li "Revoke access to the workspace, conversations and all phases (e.g. Pre-Consultation, Application, Work)"]
    [:li "Remove any invites to the workspaces"]
    [:li "Revoke access to the project if it was the last remaining workspace on said project"]
    [:li "Remove the workspace from the dashboard of that user"]
    [:li "Disable notifications to that user for that workspace"]
    [:li "Mark the user as 'removed' on the workspace for remaining users"]]

   [:p "If the user is a member of groups (e.g. authority groups, review groups), they will still have whatever access is given via membership to those groups. It is possible to remove the last party from e.g. a draft application, in which case no-one can access it anymore! Be careful!"]])

(defn user-info []
  (let [{:keys [:user/stakeholder? :user/authority? :user/last-login]
         :as   user}                                                  (<sub [::user])]
    (when user
      [:div.mt-8
       [:h3 (user-model/full-name user)]
       (when stakeholder?
         [:div
          [:span.font-bold.text-ui-red.pr-2 "User is in an approver group!"]
          "Use the group removal functionality to remove those."])
       (when authority?
         [:div
          [:span.font-bold.text-ui-red.pr-2 "User is in an authority group!"]
          "Use the group removal functionality to remove those."])
       [:div.py-2 "Last login: " (str last-login)]])))

(defn wizard []
  (let [email (<sub [::email])]
    [:section
     [:h2 "Remove user from workspaces and groups"]
     [:form.flex.items-end.mb-4
      {:on-submit (fn [ev]
                    (.preventDefault ev)
                    (>evt [::find-user email]))}
      [ui/text-input
       {:id         "remove-user-email"
        :label-text "Email address of the user"
        :on-change  #(rf/dispatch-sync [::set-email (futil/tval %)])
        :value      email}]
      [:div.ml-2
       (cc/button
         {:type     :submit
          :disabled (not (<sub [::email-valid?]))}
         "Find user")]]

     [user-info]
     [workspaces]
     [groups]]))


(defn view [match]
  [acommon/admin-page
   (util/mmap {:title "Remove user from workspace and groups"} match)
   [help-text]
   [wizard]])

(def route
  {:name :view/admin.commands.remove-user-from-ws
   :view view})
